/**
 * Storage reconciliation — the arithmetic that makes the usage drill-down add up to the ring
 * above it (app#135, finding DS-16).
 *
 * The org's storage axis has TWO trees, and has had since router migration 0086: the org's
 * NETWORKS (`network_metrics.storage_bytes_max`) and the org's NODES (`node_storage_daily`).
 * `get_org_usage` — the ring — takes the period max per SUBJECT and then sums ACROSS subjects,
 * so the ring is `sum(network peaks) + sum(node peaks)`.
 *
 * The two halves are read by two different RPCs, and neither can carry the other:
 *   - `get_usage_summary` returns the NETWORK half, one row per network.
 *   - `get_node_storage_summary` (router 0087) returns the NODE half, one row per node.
 *
 * Node storage follows the MACHINE and belongs to no network, so it cannot be split per network
 * without inventing an answer. That is why the rows never added up: the screen showed one half
 * of a two-half sum and said nothing about the other.
 *
 * The rule, stated once: a per-NODE fact rolls to the ORG and cannot be split per network without
 * double-counting; a per-NETWORK fact is genuinely per-network. A screen that mixes them must say
 * which axis each number is on.
 */

/** The only field this arithmetic needs. `NetworkUsage` and `NodeStorage` both satisfy it. */
export type StoragePeak = { storageBytesPeak: number }

export type StorageReconciliation = {
  /** Sum of the per-network peaks shown in the drill-down. */
  networkBytes: number
  /** Sum of the per-node peaks shown in the drill-down. */
  nodeBytes: number
  /** networkBytes + nodeBytes — what the rows on screen account for. */
  accountedBytes: number
  /** The org storage axis from `get_org_usage` — the ring, and the invoice. */
  ringBytes: number
  /** ringBytes - accountedBytes. Positive ⇒ the ring counts bytes no row on screen names. */
  residualBytes: number
  /** |residual| is within tolerance, so the rows may be presented as adding up. */
  reconciles: boolean
}

/**
 * Tolerance. The ring and the two drill-downs are three separate RPC calls, issued together but
 * evaluated at three instants; a daily rollup written between them moves one and not the others.
 * An exact-equality check would therefore report a mismatch that is really a clock. Relative,
 * with an absolute floor so a small org is not held to a fraction of a byte.
 */
export const RECONCILE_TOLERANCE_BYTES = 1024 * 1024
export const RECONCILE_TOLERANCE_FRACTION = 0.005

const sumPeaks = (rows: readonly StoragePeak[]): number =>
  rows.reduce((total, row) => total + (Number.isFinite(row.storageBytesPeak) ? row.storageBytesPeak : 0), 0)

/**
 * Reconcile the two drill-down halves against the org storage ring.
 *
 * ⚠ SUM across subjects, never MAX: each row is already a per-subject period maximum, and the
 * ring is built the same way (router 0086's per-subject-max-then-sum). Taking a cross-subject max
 * here would undercount every org that has more than one network or node.
 *
 * `ringBytes` is passed in rather than derived, because the ring is what the customer is billed
 * and the rows are what the customer is shown — the point of this function is to compare them.
 */
export const reconcileStorage = (
  networkRows: readonly StoragePeak[],
  nodeRows: readonly StoragePeak[],
  ringBytes: number
): StorageReconciliation => {
  const networkBytes = sumPeaks(networkRows)
  const nodeBytes = sumPeaks(nodeRows)
  const accountedBytes = networkBytes + nodeBytes
  const ring = Number.isFinite(ringBytes) ? ringBytes : 0
  const residualBytes = ring - accountedBytes
  const tolerance = Math.max(RECONCILE_TOLERANCE_BYTES, ring * RECONCILE_TOLERANCE_FRACTION)
  return {
    networkBytes,
    nodeBytes,
    accountedBytes,
    ringBytes: ring,
    residualBytes,
    reconciles: Math.abs(residualBytes) <= tolerance,
  }
}
